'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, MapPin, Clock, ArrowRight, Briefcase, DollarSign } from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { careersApi } from '@/lib/api'
import type { Career } from '@/lib/types'

export default function Careers() {
  const [careers, setCareers] = useState<Career[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchCareers = async () => {
      try {
        const { data, error } = await careersApi.getAll({ active: true })
        
        if (error) throw new Error(error)
        setCareers(data || [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch careers')
      } finally {
        setLoading(false)
      }
    }

    fetchCareers()
  }, [])

  const formatEmploymentType = (type: string) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Join Our <span className="gradient-text">Mission</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Help us build the future of digital education and privacy-first technology. 
                Join a team that&apos;s passionate about empowering the next generation while protecting their privacy.
              </p>
            </motion.div>

          </div>
        </section>

        {/* Current Opportunities */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Current <span className="gradient-text">Opportunities</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {loading ? 'Loading current opportunities...' : 
                 careers.length > 0 ? 'Join our team and help build the future of EdTech and privacy technology.' :
                 'We\'re currently focused on building our core products and don\'t have any open positions at this time.'}
              </p>
            </motion.div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">Error loading careers: {error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            ) : careers.length > 0 ? (
              /* Open Positions */
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-1">
                {careers.map((career, index) => (
                  <motion.div
                    key={career.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white border border-gray-200 rounded-3xl p-8 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
                      <div className="mb-4 lg:mb-0">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{career.title}</h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {career.department}
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {career.location}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatEmploymentType(career.type)}
                          </div>
                          {career.remote_allowed && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                              Remote Friendly
                            </span>
                          )}
                        </div>
                      </div>
                      {career.salary_range && (
                        <div className="flex items-center gap-1 text-lg font-semibold text-green-600">
                          <DollarSign className="w-5 h-5" />
                          {career.salary_range}
                        </div>
                      )}
                    </div>

                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {career.description}
                    </p>

                    {career.requirements && career.requirements.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Requirements:</h4>
                        <ul className="space-y-2">
                          {career.requirements.map((req, reqIndex) => (
                            <li key={reqIndex} className="flex items-start gap-2 text-gray-600">
                              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {career.responsibilities && career.responsibilities.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Responsibilities:</h4>
                        <ul className="space-y-2">
                          {career.responsibilities.map((resp, respIndex) => (
                            <li key={respIndex} className="flex items-start gap-2 text-gray-600">
                              <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <motion.a
                      href={career.application_url || "/#contact"}
                      className="btn-primary inline-flex items-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Apply Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </motion.a>
                  </motion.div>
                ))}
              </div>
            ) : (
              /* No Positions Available */
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-3xl p-12 mb-16">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">No Open Positions</h3>
                  <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                    As an early-stage company in pre-production development, we&apos;re currently building our foundational 
                    products with our core team. We&apos;re not actively hiring at this time, but we&apos;re always interested 
                    in connecting with exceptional talent for future opportunities.
                  </p>
                  <div className="bg-white rounded-2xl p-6 max-w-md mx-auto">
                    <h4 className="font-semibold text-gray-800 mb-3">Stay Connected</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Follow our progress and be the first to know when we start hiring.
                    </p>
                    <motion.a
                      href="/#contact"
                      className="btn-primary inline-flex items-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Get Notified
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </motion.a>
                  </div>
                </div>

                {/* Future Opportunities */}
                <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Interested in Our Mission?</h3>
                  <p className="text-lg mb-6 opacity-90">
                    Even though we don&apos;t have open positions right now, we&apos;d love to hear from talented individuals 
                    who are passionate about EdTech and privacy-first technology. Send us your information and 
                    we&apos;ll reach out when the right opportunity arises.
                  </p>
                  <motion.a
                    href="/#contact"
                    className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-2xl hover:bg-gray-50 transition-colors inline-flex items-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Express Interest
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </motion.a>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}
